import React, { useEffect, useState } from 'react';
import {
  SpaceBetween,
  Header,
  Box,
  Button,
  Spinner,
  Alert,
  FormField,
  StatusIndicator,
  Icon,
} from '@cloudscape-design/components';
import { questionService } from 'src/data/api/helios-service/questionService';
import { questionResponseService } from 'src/data/api/helios-service/questionResponseService';
import { Question } from 'src/types/helios/Question';
import { AuditQuestionItemResponse } from 'src/types/helios/QuestionResponse';
import SafeHtml from '../SafeHtml';
import { FileMetadata } from '../FileDisplay';
import routePaths from 'src/contstant/routePaths';
import useUserProfile from 'src/hooks/useUserProfile';
import { getQuestionContent } from 'src/helper/questionHelper';
import { convertMediaResponseToMediaData } from 'src/utils/mediaUtils';
import { isImageExtension } from 'src/contstant/fileConstants';
import './TaskAuditQuestionResponsePanel.scss';

interface TaskAuditQuestionResponsePanelProps {
  questionId: string;
  questionVersion?: string;
  subQuestionId?: string;
  auditId?: string;
  businessUnit: string;
  divisionName?: string;
  programId: string;
  /** Optional header text displayed above the panel content */
  headerText?: string;
  /** Optional description text displayed below the header */
  descriptionText?: string;
}

/**
 * Side panel component that displays question details and audit response information
 * for a task. Shows question text, pass/fail criteria, SOPs, and audit evidence.
 */
export const TaskAuditQuestionResponsePanel: React.FC<
  TaskAuditQuestionResponsePanelProps
> = ({
  questionId,
  questionVersion,
  subQuestionId,
  auditId,
  businessUnit,
  divisionName,
  programId,
  headerText,
  descriptionText,
}) => {
  const { getUserSelectedLanguage } = useUserProfile();
  const [question, setQuestion] = useState<Question | null>(null);
  const [auditResponse, setAuditResponse] = useState<AuditQuestionItemResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch question details
        const questionResponse = await questionService.getById({
          programId,
          questionId,
          divisionName: divisionName || '',
          businessUnit,
          language: getUserSelectedLanguage(),
          version: questionVersion,
        });

        setQuestion(questionResponse.question);

        // Fetch audit response if auditId is provided
        if (auditId) {
          const responseData = await questionResponseService.getById({
            businessUnitId: businessUnit,
            auditId,
            questionId,
          });

          setAuditResponse(responseData.auditQuestionResponse);
        }
      } catch (err) {
        console.error('Error fetching question/audit details:', err);
        setError('Failed to load question details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId, questionVersion, auditId, businessUnit, divisionName, programId]);

  const handleViewAudit = () => {
    if (!auditId) return;
    const params = new URLSearchParams();
    params.set('auditId', auditId);
    params.set('businessUnit', businessUnit);
    const url = `${routePaths.PERFORM_AUDIT.path}?${params}`;
    window.open(url, '_blank');
  };

  // Get the question content using the helper (handles localization)
  const questionContent = question
    ? getQuestionContent(question, getUserSelectedLanguage())
    : null;

  // Get the appropriate sub-question if subQuestionId is provided
  const subQuestion = subQuestionId
    ? questionContent?.subQuestions?.find((sq) => sq.subQuestionId === subQuestionId)
    : null;

  // Get the appropriate sub-response if subQuestionId is provided
  const subResponse = subQuestionId
    ? auditResponse?.subResponses?.find((sr) => sr.subQuestionId === subQuestionId)
    : null;

  /**
   * Sorts media files to display images before documents
   * Creates a copy to avoid mutating the original array
   * @param files - Array of file metadata to sort
   * @returns Sorted array with images first, then documents
   */
  const sortMediaFiles = (files: FileMetadata[]): FileMetadata[] => {
    return [...files].sort((a, b) => {
      const aIsImage = isImageExtension(a.extension || '');
      const bIsImage = isImageExtension(b.extension || '');
      if (aIsImage && !bIsImage) return -1;
      if (!aIsImage && bIsImage) return 1;
      return 0;
    });
  };

  // Extract media files from audit response
  const responseMediaFiles: FileMetadata[] = React.useMemo(() => {
    // For sub-questions, get media from the sub-response's mediaResponses
    if (subQuestionId && subResponse) {
      const subQuestionMedia = subResponse.mediaResponses?.response;

      if (!subQuestionMedia) {
        return [];
      }

      const files = subQuestionMedia.map((mediaData) => ({
        fileName: mediaData.media.fileName || 'document',
        mediaId: mediaData.media.mediaId,
        presignedUrl: mediaData.presignedUrl,
        extension: mediaData.media.extension,
      }));

      return sortMediaFiles(files);
    }

    // Otherwise, get media from the main response
    const mainResponseMedia = auditResponse?.mediaResponses?.response;

    if (!mainResponseMedia) {
      return [];
    }

    const files = mainResponseMedia.map((mediaData) => ({
      fileName: mediaData.media.fileName || 'document',
      mediaId: mediaData.media.mediaId,
      presignedUrl: mediaData.presignedUrl,
      extension: mediaData.media.extension,
    }));

    return sortMediaFiles(files);
  }, [auditResponse?.mediaResponses, subResponse, subQuestionId]);

  if (loading) {
    return (
      <div className="task-audit-question-response-panel">
        <div className="panel-section">
          <SpaceBetween size="m" alignItems="center">
            <Spinner size="large" />
            <Box fontSize="heading-m" color="text-body-secondary">
              Loading question details...
            </Box>
          </SpaceBetween>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="task-audit-question-response-panel">
        <div className="panel-section">
          <Alert type="error" header="Error">
            {error}
          </Alert>
        </div>
      </div>
    );
  }

  if (!question || !questionContent) {
    return (
      <div className="task-audit-question-response-panel">
        <div className="panel-section">
          <Alert type="error" header="Unexpected Error">
            Unable to load question details. Please try again or contact support if the
            issue persists.
          </Alert>
        </div>
      </div>
    );
  }

  const questionDetails = questionContent.questionDetails;

  return (
    <div className="task-audit-question-response-panel">
      {/* Optional Header / Description */}
      {(headerText || descriptionText) && (
        <div className="panel-section">
          {headerText && <Header variant="h3">{headerText}</Header>}
          <Box color="text-body-secondary" fontSize="body-m">
            {descriptionText || 'Question context and audit response for this task.'}
          </Box>
        </div>
      )}

      {/* Question Details Header — grey bar */}
      <div className="panel-section-header">Question Details</div>

      {/* Question Details Content — white */}
      <div className="panel-section">
        <SpaceBetween size="s">
          <div className="flex-container">
            <div className="flex-item">
              <Box variant="awsui-key-label" fontSize="body-s">
                Question ID
              </Box>
              <Box>{questionId}</Box>
            </div>
            <div className="flex-item">
              <Box variant="awsui-key-label" fontSize="body-s">
                Question Version
              </Box>
              <Box>{question.version || '-'}</Box>
            </div>
          </div>

          <Box>
            <Box variant="awsui-key-label" fontSize="body-s" padding={{ bottom: 'xxxs' }}>
              Audit Question
            </Box>
            <SafeHtml
              html={questionDetails?.questionText || ''}
              mediaResponse={convertMediaResponseToMediaData(
                question.mediaResponses?.questionText,
              )}
            />
          </Box>

          <Box>
            <div className="icon-with-label">
              <StatusIndicator type="success" />
              <Box variant="awsui-key-label" fontSize="body-s" margin={{ left: 'xxs' }}>
                Pass Criteria
              </Box>
            </div>
            <SafeHtml
              html={questionDetails?.passCriteria || ''}
              mediaResponse={convertMediaResponseToMediaData(
                question.mediaResponses?.passCriteria,
              )}
            />
          </Box>

          <Box>
            <div className="icon-with-label">
              <StatusIndicator type="error" />
              <Box variant="awsui-key-label" fontSize="body-s" margin={{ left: 'xxs' }}>
                Fail Criteria
              </Box>
            </div>
            <SafeHtml
              html={questionDetails?.failCriteria || ''}
              mediaResponse={convertMediaResponseToMediaData(
                question.mediaResponses?.failCriteria,
              )}
            />
          </Box>

          {/* Sub-Question Details (if applicable) */}
        </SpaceBetween>
      </div>

      {subQuestion && (
        <>
          <div className="panel-section-header">Sub Question</div>
          <div className="panel-section">
            <SpaceBetween size="s">
              <Box>
                <Box variant="awsui-key-label" fontSize="body-s">
                  Sub-Question ID
                </Box>
                <Box>{subQuestionId}</Box>
              </Box>
              {subQuestion.instruction && (
                <Box>
                  <Box
                    variant="awsui-key-label"
                    fontSize="body-s"
                    padding={{ bottom: 'xxxs' }}
                  >
                    Audit Sub Question
                  </Box>
                  <SafeHtml
                    html={subQuestion.instruction || ''}
                    mediaResponse={convertMediaResponseToMediaData(
                      subQuestion.mediaResponsesMap?.instruction,
                    )}
                  />
                </Box>
              )}
            </SpaceBetween>
          </div>
        </>
      )}

      {/* Question Details continued — SOPs */}
      <div className="panel-section">
        <SpaceBetween size="s">
          <Box>
            <Box variant="awsui-key-label" fontSize="body-s" padding={{ bottom: 'xxxs' }}>
              Standard Operating Procedure
            </Box>
            {question.docLinks && question.docLinks.length > 0 ? (
              <SpaceBetween size="xs">
                {question.docLinks.map((docLink, index) => (
                  <Box key={`main-${index}`}>
                    {docLink.link ? (
                      <a
                        href={docLink.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sop-link-container"
                      >
                        <Icon name="external" size="small" />
                        {docLink.title || docLink.link}
                      </a>
                    ) : (
                      <span className="sop-text-container">
                        <Icon name="file" size="small" />
                        {docLink.title || '-'}
                      </span>
                    )}
                  </Box>
                ))}
              </SpaceBetween>
            ) : (
              <Box color="text-body-secondary">No SOPs available</Box>
            )}
          </Box>
        </SpaceBetween>
      </div>

      {/* Audit Response & Evidence */}
      {auditResponse && (
        <>
          {/* Header — grey bar */}
          <div className="panel-section-header">Audit Response & Evidence</div>

          {/* Content — white */}
          <div className="panel-section">
            <SpaceBetween size="s">
              <FormField label="Response Status">
                <StatusIndicator
                  type={(() => {
                    const result = subQuestionId
                      ? subResponse?.result
                      : auditResponse.responseResult;
                    return result === 'PASS'
                      ? 'success'
                      : result === 'FAIL'
                        ? 'error'
                        : 'info';
                  })()}
                >
                  {subQuestionId
                    ? subResponse?.result || 'N/A'
                    : auditResponse.responseResult || 'N/A'}
                </StatusIndicator>
              </FormField>

              <FormField label="Auditor Comments">
                {(() => {
                  if (subQuestionId) {
                    return subResponse?.comment ? (
                      <div className="comment-box">{subResponse.comment}</div>
                    ) : (
                      <Box color="text-body-secondary">No comments provided</Box>
                    );
                  }
                  return auditResponse.response?.comment ? (
                    <div className="comment-box">{auditResponse.response.comment}</div>
                  ) : (
                    <Box color="text-body-secondary">No comments provided</Box>
                  );
                })()}
              </FormField>

              <FormField label="Reference from Audit (Media)">
                {responseMediaFiles.length > 0 ? (
                  <SpaceBetween size="s">
                    {responseMediaFiles.map((file, index) => {
                      const isImage = isImageExtension(file.extension || '');
                      const displayName = file.fileName || 'document';

                      if (isImage && file.presignedUrl) {
                        return (
                          <a
                            key={index}
                            href={file.presignedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="media-link-block"
                          >
                            <div className="media-image-container">
                              <img
                                src={file.presignedUrl}
                                alt={displayName}
                                className="media-image"
                              />
                              <div className="media-image-footer">
                                <div className="media-file-content">
                                  <Icon name="file" size="small" />
                                  <span>{displayName}</span>
                                </div>
                                <Icon name="external" size="small" />
                              </div>
                            </div>
                          </a>
                        );
                      } else {
                        return (
                          <a
                            key={index}
                            href={file.presignedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="media-file-link"
                          >
                            <div className="media-file-box">
                              <div className="media-file-content">
                                <Icon name="file" size="small" />
                                <span>{displayName}</span>
                              </div>
                              <Icon name="external" size="small" />
                            </div>
                          </a>
                        );
                      }
                    })}
                  </SpaceBetween>
                ) : (
                  <Box color="text-body-secondary">No media available</Box>
                )}
              </FormField>
            </SpaceBetween>
          </div>
        </>
      )}

      {/* View Full Audit Button */}
      {auditId && (
        <div className="panel-section">
          <Box textAlign="center">
            <Button variant="primary" onClick={handleViewAudit} iconName="external">
              Navigate to Full Audit
            </Button>
          </Box>
        </div>
      )}
    </div>
  );
};
